// Chrona Pro configurator telemetry — the ONLY file in src/web/ that
// calls posthog.capture(). All instrumentation goes through the Telemetry
// object below. See docs/skills/telemetry/SKILL.md for the project-wide
// conventions this file follows.
//
// Surface: pro_configurator. Events are `object_verb` in snake_case per
// the skill; surface disambiguation is a property, not a prefix.

import posthog from 'posthog-js'
import type {
  ConfiguratorViewedProps,
  ConfiguratorTagSelectedProps,
  ConfiguratorTagDeselectedProps,
  ConfiguratorFreetextSubmittedProps,
  ConfiguratorRuleAddedProps,
  ConfiguratorRuleRemovedProps,
  ConfiguratorSignupStartedProps,
  ConfiguratorSignupCompletedProps,
  ConfiguratorInstallAttemptedProps,
  ConfiguratorProfileSavedProps,
  ConfiguratorPackToggledProps,
  OnboardingPageViewedProps,
  OnboardingFreetextSubmittedProps,
  OnboardingPackToggledProps,
  OnboardingCustomRuleAddedProps,
  OnboardingSignupSubmittedProps,
  OnboardingSuccessViewedProps,
  OnboardingTranslateFailedProps,
  OnboardingSignupFailedProps,
  OnboardingNetworkErrorProps,
  OnboardingInstallPageViewedProps,
  OnboardingDoctypeSelectedProps,
  OnboardingFormattingPillAddedProps,
  OnboardingFormattingPillRemovedProps,
  OnboardingFormatFieldChangedProps,
  OnboardingSectionFocusedProps,
  ConfiguratorPathLoadedProps,
} from './telemetry-types'

const POSTHOG_API_KEY =
  (import.meta.env.VITE_POSTHOG_API_KEY as string | undefined) ?? ''
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://us.i.posthog.com'
const APP_VERSION =
  (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'unknown'
const SURFACE = 'pro_configurator'

let initialized = false

// ── Session store (module-level, no React hooks) ─────────────────────

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16)
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

let sessionId = ''
let currentScreen = ''
let contextUserId = 'anonymous'

export function initTelemetry(): void {
  if (initialized) return
  if (!POSTHOG_API_KEY) {
    console.warn('[chrona:pro-configurator] PostHog key missing — telemetry disabled')
    return
  }
  try {
    posthog.init(POSTHOG_API_KEY, {
      api_host: POSTHOG_HOST,
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      disable_session_recording: true,
    })
    initialized = true
    sessionId = generateUUID()
  } catch (err) {
    // Telemetry must never block features.
    console.warn('[chrona:pro-configurator] PostHog init failed', err)
  }
}

export function setCurrentScreen(path: string): void {
  currentScreen = path
}

/**
 * Call when a Chrona user id becomes known (post-sign-in).
 * The id MUST be the Chrona user UUID from /ribbon/session, not the
 * Supabase auth.uid() — unifies identity across Chrona surfaces per
 * A2F-434.
 */
export function identifyUser(chronaUserId: string): void {
  if (!initialized) return
  if (!chronaUserId) return
  try {
    contextUserId = chronaUserId
    posthog.identify(chronaUserId)
  } catch {
    // never block features
  }
}

/**
 * Set PostHog person properties on the current distinct id. Used by
 * A2F-540 to attach the real display_name / organization / role_title
 * after /install hydrates — these come from the SignupModal form, not
 * from any server response, so we route them through here rather than
 * piggy-backing on identifyUser.
 */
export function setPersonProperties(properties: Record<string, unknown>): void {
  if (!initialized) return
  try {
    posthog.setPersonProperties(properties)
  } catch {
    // never block features
  }
}

/**
 * Call on sign-out. Clears the PostHog identity AND regenerates the
 * local session id so a subsequent sign-in on the same page doesn't
 * carry the prior user's session_id into the new user's events.
 */
export function resetTelemetry(): void {
  if (!initialized) return
  try {
    contextUserId = 'anonymous'
    sessionId = generateUUID()
    posthog.reset()
  } catch {
    // never block features
  }
}

// ── Common props merged into every event ──────────────────────────────

function commonProps(): Record<string, unknown> {
  return {
    surface: SURFACE,
    user_id: contextUserId,
    session_id: sessionId,
    screen_name: currentScreen,
    app_version: APP_VERSION,
    // Partition events by deploy environment in PostHog. Sourced from
    // Vercel's VERCEL_ENV at build time via vite.config.ts. See A2F-785.
    env: __APP_ENV__,
  }
}

function capture(event: string, props: object = {}): void {
  if (!initialized) return
  try {
    posthog.capture(event, { ...commonProps(), ...(props as Record<string, unknown>) })
  } catch {
    // never block features
  }
}

// ── Public API — one named function per event ────────────────────────

export const Telemetry = {
  identify: identifyUser,
  setPersonProperties,
  reset: resetTelemetry,
  setScreen: setCurrentScreen,

  configuratorViewed: (p: ConfiguratorViewedProps) =>
    capture('configurator_viewed', p),
  // A2F-766: one-shot mount event with `path: 'signup' | 'edit'`
  // (replaces the per-version `configurator_variant` prop now that
  // /pro-v2 collapsed into the unified surface).
  configuratorPathLoaded: (p: ConfiguratorPathLoadedProps) =>
    capture('configurator_path_loaded', p),
  configuratorTagSelected: (p: ConfiguratorTagSelectedProps) =>
    capture('configurator_tag_selected', p),
  configuratorTagDeselected: (p: ConfiguratorTagDeselectedProps) =>
    capture('configurator_tag_deselected', p),
  configuratorFreetextSubmitted: (p: ConfiguratorFreetextSubmittedProps) =>
    capture('configurator_freetext_submitted', p),
  configuratorRuleAdded: (p: ConfiguratorRuleAddedProps) =>
    capture('configurator_rule_added', p),
  configuratorRuleRemoved: (p: ConfiguratorRuleRemovedProps) =>
    capture('configurator_rule_removed', p),
  configuratorSignupStarted: (p: ConfiguratorSignupStartedProps) =>
    capture('configurator_signup_started', p),
  configuratorSignupCompleted: (p: ConfiguratorSignupCompletedProps) =>
    capture('configurator_signup_completed', p),
  configuratorInstallAttempted: (p: ConfiguratorInstallAttemptedProps) =>
    capture('configurator_install_attempted', p),
  configuratorProfileSaved: (p: ConfiguratorProfileSavedProps) =>
    capture('configurator_profile_saved', p),
  configuratorPackToggled: (p: ConfiguratorPackToggledProps) =>
    capture('configurator_pack_toggled', p),

  // ── A2F-546 / A2F-650 onboarding events (surface: 'pro_onboarding') ─
  // Error variants ship alongside success variants per F5.
  onboardingPageViewed: (p: OnboardingPageViewedProps) =>
    captureOnboarding('onboarding_page_viewed', p),
  onboardingFreetextSubmitted: (p: OnboardingFreetextSubmittedProps) =>
    captureOnboarding('onboarding_freetext_submitted', p),
  onboardingPackToggled: (p: OnboardingPackToggledProps) =>
    captureOnboarding('onboarding_pack_toggled', p),
  onboardingCustomRuleAdded: (p: OnboardingCustomRuleAddedProps) =>
    captureOnboarding('onboarding_custom_rule_added', p),
  onboardingSignupSubmitted: (p: OnboardingSignupSubmittedProps) =>
    captureOnboarding('onboarding_signup_submitted', p),
  onboardingSuccessViewed: (p: OnboardingSuccessViewedProps) =>
    captureOnboarding('onboarding_success_viewed', p),
  onboardingTranslateFailed: (p: OnboardingTranslateFailedProps) =>
    captureOnboarding('onboarding_translate_failed', p),
  onboardingSignupFailed: (p: OnboardingSignupFailedProps) =>
    captureOnboarding('onboarding_signup_failed', p),
  onboardingNetworkError: (p: OnboardingNetworkErrorProps) =>
    captureOnboarding('onboarding_network_error', p),
  // Batch 4.5: InstallPage mount event carrying corr_B.
  onboardingInstallPageViewed: (p: OnboardingInstallPageViewedProps) =>
    captureOnboarding('onboarding_install_page_viewed', p),

  // ── A2F-650 FRE redesign events ──────────────────────────────────
  onboardingDoctypeSelected: (p: OnboardingDoctypeSelectedProps) =>
    captureOnboarding('onboarding_doctype_selected', p),
  onboardingFormattingPillAdded: (p: OnboardingFormattingPillAddedProps) =>
    captureOnboarding('onboarding_formatting_pill_added', p),
  onboardingFormattingPillRemoved: (p: OnboardingFormattingPillRemovedProps) =>
    captureOnboarding('onboarding_formatting_pill_removed', p),
  onboardingFormatFieldChanged: (p: OnboardingFormatFieldChangedProps) =>
    captureOnboarding('onboarding_format_field_changed', p),

  // A2F-764: caller MUST throttle (once per section_id per page-view).
  onboardingSectionFocused: (p: OnboardingSectionFocusedProps) =>
    captureOnboarding('onboarding_section_focused', p),
}

// Onboarding events carry `surface: 'pro_onboarding'` so PostHog can
// split the funnel from configurator_* events (which stay on
// pro_configurator). Both surfaces share the module-level `commonProps`
// via the inner capture() call.
function captureOnboarding(event: string, props: object): void {
  capture(event, { surface: 'pro_onboarding', ...(props as Record<string, unknown>) })
}
